import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import { useTranslation } from 'react-i18next';
import { colors, getScoreColor } from '../../theme';
import { getMarketStageLabel } from '../../utils/marketStageLabels';

/* ═══════════════════════════════════════════════════════════════
 * Strategy configuration
 * ═══════════════════════════════════════════════════════════════ */
const STRATEGIES = {
  FLOW_LEADERS:            { Icon: TrendingUpIcon,  color: colors.flowLeaders.main,          nameKey: 'vnstock.recommendations.styles.flowLeaders' },
  QUALITY_GROWTH:          { Icon: AutoAwesomeIcon,  color: colors.qualityGrowth.main,        nameKey: 'vnstock.recommendations.styles.qualityGrowth' },
  VALUE_WITH_CONFIRMATION: { Icon: AssessmentIcon,   color: colors.valueConfirmation.main,    nameKey: 'vnstock.recommendations.styles.valueConfirmation' },
  TURNAROUND:              { Icon: ShowChartIcon,    color: colors.turnaround.main,           nameKey: 'vnstock.recommendations.styles.turnaround' },
  ACCUMULATION_BREAKOUT:   { Icon: TimelineIcon,     color: colors.accumulationBreakout.main, nameKey: 'vnstock.recommendations.styles.accumulationBreakout' },
  REVERSAL:                { Icon: SwapHorizIcon,    color: colors.reversal.main,             nameKey: 'vnstock.recommendations.styles.reversal' },
  SUPER_STOCK:             { Icon: RocketLaunchIcon, color: colors.superStock.main,           nameKey: 'vnstock.recommendations.styles.superStock' },
  PRICE_ACTION:            { Icon: CandlestickChartIcon, color: colors.priceAction.main,      nameKey: 'vnstock.recommendations.styles.priceAction' },
};

/* ═══════════════════════════════════════════════════════════════
 * Helpers
 * ═══════════════════════════════════════════════════════════════ */
const fmtMCap = (v) => {
  if (v == null) return '—';
  const n = typeof v === 'string' ? Number(v) : v;
  if (isNaN(n)) return String(v);
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (n >= 1e9)  return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6)  return `${(n / 1e6).toFixed(0)}M`;
  return n.toLocaleString();
};

const fmtSector = (sector, lang = 'vi') => {
  if (!sector) return '—';
  if (typeof sector === 'string') return sector;
  return lang === 'vi' ? (sector.vi || sector.en || '—') : (sector.en || sector.vi || '—');
};

const getReasonStr = (reason, lang = 'vi') => {
  if (typeof reason === 'string') return reason;
  if (reason && typeof reason === 'object') return reason[lang] || reason.vi || reason.en || '';
  return '';
};

const getLocalizedCopy = (value, lang = 'vi') => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return value[lang] || value.vi || value.en || '';
  return '';
};

const getVisibleMarketStageOverlay = (overlay, lang = 'vi') => {
  const overlayText = getLocalizedCopy(overlay?.explanation, lang);
  const overlayPct = overlay?.adjustment_pct;
  const isMeaningful = typeof overlayPct === 'number' && Math.abs(overlayPct) >= 2.5;
  if (!overlayText || !isMeaningful) {
    return {
      visible: false,
      text: overlayText,
      pct: overlayPct,
      positive: typeof overlayPct === 'number' ? overlayPct >= 0 : true,
    };
  }
  return {
    visible: true,
    text: overlayText,
    pct: overlayPct,
    positive: overlayPct >= 0,
  };
};

const getOverlaySignature = (overlay) => {
  if (!overlay?.visible) return null;
  const pct = typeof overlay.pct === 'number' ? overlay.pct.toFixed(1) : 'na';
  return `${overlay.text}__${pct}__${overlay.positive ? 'pos' : 'neg'}`;
};

const getCompactMarketStageSummary = (marketStage, lang = 'vi') => {
  const exposure = marketStage?.exposure_target_pct;
  const exposureText = typeof exposure === 'number' ? `${exposure.toFixed(1)}%` : null;

  if (lang === 'vi') {
    let guidance = 'Ưu tiên các setup đi đúng pha hiện tại';
    if (typeof exposure === 'number') {
      if (exposure >= 75) guidance = 'Có thể chủ động hơn với các mã mạnh đúng pha';
      else if (exposure >= 55) guidance = 'Ưu tiên các setup mạnh và giữ độ chọn lọc';
      else if (exposure >= 35) guidance = 'Giữ chọn lọc cao, chỉ ưu tiên các mã thật sự nổi bật';
      else guidance = 'Thiên về phòng thủ, kiên nhẫn chờ tín hiệu rõ hơn';
    }
    return exposureText ? `${guidance}. Tỷ trọng cổ phiếu tham khảo khoảng ${exposureText}.` : guidance;
  }

  let guidance = 'Favor setups that align with the current phase';
  if (typeof exposure === 'number') {
    if (exposure >= 75) guidance = 'You can lean in more on strong names aligned with this phase';
    else if (exposure >= 55) guidance = 'Prefer stronger setups while keeping selectivity high';
    else if (exposure >= 35) guidance = 'Stay selective and focus only on the clearest names';
    else guidance = 'Stay defensive and wait for cleaner confirmation';
  }
  return exposureText ? `${guidance}. Suggested equity allocation is around ${exposureText}.` : guidance;
};

const RANK_BG = [
  'linear-gradient(135deg, #fbbf24, #f59e0b)', // gold
  'linear-gradient(135deg, #cbd5e1, #94a3b8)', // silver
  'linear-gradient(135deg, #fdba74, #f97316)', // bronze
];

const FLAG_COLORS = {
  HIGH_VOLATILITY:      { bg: '#fef3c7', color: '#d97706' },
  HIGH_LEVERAGE:        { bg: '#fef3c7', color: '#d97706' },
  HIGH_VALUATION:       { bg: '#fef3c7', color: '#d97706' },
  LARGE_DRAWDOWN:       { bg: '#fef2f2', color: '#dc2626' },
  OVEREXTENDED_20D:     { bg: '#fef2f2', color: '#dc2626' },
  FAR_FROM_MA50:        { bg: '#fef2f2', color: '#dc2626' },
  CLIMAX_TOP:           { bg: '#fce7f3', color: '#be185d' },
  STALLING_DETECTED:    { bg: '#fce7f3', color: '#be185d' },
  EXHAUSTION_GAP:       { bg: '#fce7f3', color: '#be185d' },
  CHURNING:             { bg: '#fce7f3', color: '#be185d' },
  HEAVY_DISTRIBUTION:   { bg: '#fef2f2', color: '#dc2626' },
  UPPER_SHADOW_WARNING: { bg: '#fef3c7', color: '#d97706' },
  MARKET_RISK_OFF:      { bg: '#eff6ff', color: '#2563eb' },
};

/* ═══════════════════════════════════════════════════════════════
 * Main Component
 * ═══════════════════════════════════════════════════════════════ */
const RecommendationsView = ({
  recommendations = {},
  currentState = {},
  activeStyle: activeStyleProp,
  setActiveStyle: setActiveStyleProp,
  loading = false,
  hideHeader = false,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [localActiveStyle, setLocalActiveStyle] = useState('FLOW_LEADERS');
  const activeStyle = activeStyleProp !== undefined ? activeStyleProp : localActiveStyle;
  const setActiveStyle = setActiveStyleProp || setLocalActiveStyle;
  const lang = i18n.language || 'vi';

  /* ─── Scroll restoration ─── */
  useEffect(() => {
    const key = `scroll_${location.pathname}`;
    const saved = sessionStorage.getItem(key);
    if (saved) {
      setTimeout(() => {
        const el = document.querySelector('.main-content');
        if (el) el.scrollTop = parseInt(saved, 10);
        else window.scrollTo(0, parseInt(saved, 10));
        sessionStorage.removeItem(key);
      }, 100);
    }
  }, [location.pathname]);

  const goToStock = (ticker) => {
    const key = `scroll_${location.pathname}`;
    const el = document.querySelector('.main-content');
    sessionStorage.setItem(key, String(el ? el.scrollTop : window.scrollY));
    navigate(`/stocks/${ticker}`, {
      state: { ...currentState, activeStyle, from: `${location.pathname}${location.search}${location.hash}` },
    });
  };

  const activeData = recommendations[activeStyle] || { results: [], description: {} };
  const results = Array.isArray(activeData.results) ? activeData.results : [];
  const activeCfg = STRATEGIES[activeStyle] || STRATEGIES.FLOW_LEADERS;
  const activeMarketStage = activeData.marketStage || null;
  const visibleRowOverlays = results
    .map((rec) => getVisibleMarketStageOverlay(rec.marketStageOverlay, lang))
    .filter((overlay) => overlay.visible);
  const sharedOverlaySignatures = Array.from(new Set(visibleRowOverlays.map(getOverlaySignature).filter(Boolean)));
  const sharedMarketStageOverlay = sharedOverlaySignatures.length === 1 ? visibleRowOverlays[0] : null;

  /* ─── Loading ─── */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={44} sx={{ color: activeCfg.color, mb: 1.5 }} />
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
            {t('vnstock.recommendations.loading', 'Đang tải...')}
          </Typography>
        </Box>
      </Box>
    );
  }

  /* ═════════════════════════════════════════════════════════════
   * Shared render helpers
   * ═════════════════════════════════════════════════════════════ */

  /* ── Sidebar strategy item (desktop) ── */
  const renderSidebarItem = (style, sc) => {
    const data = recommendations[style] || {};
    const count = (Array.isArray(data.results) ? data.results : []).length;
    const isActive = style === activeStyle;
    const name = data.name?.[lang] || t(sc.nameKey, style);
    const IconComp = sc.Icon;

    return (
      <Box
        key={style}
        onClick={() => setActiveStyle(style)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1.25, cursor: 'pointer',
          borderLeft: `3px solid ${isActive ? sc.color : 'transparent'}`,
          bgcolor: isActive ? alpha(sc.color, 0.06) : 'transparent',
          transition: 'all 0.15s ease',
          '&:hover': { bgcolor: alpha(sc.color, isActive ? 0.08 : 0.03) },
        }}
      >
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: isActive ? alpha(sc.color, 0.14) : alpha(sc.color, 0.06),
          color: isActive ? sc.color : alpha(sc.color, 0.45),
          transition: 'all 0.15s ease', flexShrink: 0,
        }}>
          <IconComp sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{
          flex: 1, minWidth: 0, fontSize: '0.78rem',
          fontWeight: isActive ? 700 : 500,
          color: isActive ? '#1e293b' : '#64748b',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          transition: 'all 0.15s ease',
        }}>
          {name}
        </Typography>
        <Box sx={{
          minWidth: 24, height: 20, borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: isActive ? sc.color : alpha(sc.color, 0.1),
          px: 0.75, flexShrink: 0,
        }}>
          <Typography sx={{
            fontSize: '0.68rem', fontWeight: 700,
            color: isActive ? '#fff' : sc.color, lineHeight: 1,
          }}>
            {count}
          </Typography>
        </Box>
      </Box>
    );
  };

  /* ── Stock row (desktop grid) ── */
  const renderStockRow = (rec, idx) => {
    const scoreColor = getScoreColor(rec.score_level);
    const rowOverlay = getVisibleMarketStageOverlay(rec.marketStageOverlay, lang);
    const showRowOverlay = rowOverlay.visible && !sharedMarketStageOverlay;

    return (
      <Box
        key={rec.ticker || idx}
        onClick={() => goToStock(rec.ticker)}
        sx={{
          display: 'grid',
          gridTemplateColumns: '44px 1.4fr 0.9fr 0.6fr 60px 2.8fr',
          gap: '0 12px', alignItems: 'center',
          px: 2.5, py: 1.5, cursor: 'pointer',
          borderBottom: '1px solid #f1f5f9',
          transition: 'background 0.12s ease',
          '&:hover': {
            bgcolor: alpha(activeCfg.color, 0.03),
            '& .stock-ticker': { color: activeCfg.color },
          },
          ...(idx % 2 === 1 && { bgcolor: '#fafbfc' }),
        }}
      >
        {/* Rank badge */}
        <Box sx={{
          width: 28, height: 28,
          borderRadius: idx < 3 ? '50%' : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: RANK_BG[idx] || '#f1f5f9',
          color: idx < 3 ? '#fff' : '#64748b',
        }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, lineHeight: 1 }}>
            {idx + 1}
          </Typography>
        </Box>

        {/* Ticker + Company */}
        <Box sx={{ minWidth: 0 }}>
          <Typography className="stock-ticker" sx={{
            fontSize: '0.9rem', fontWeight: 800, color: '#1e293b',
            lineHeight: 1.2, transition: 'color 0.12s ease',
          }}>
            {rec.ticker}
          </Typography>
          <Typography sx={{
            fontSize: '0.7rem', color: '#94a3b8', lineHeight: 1.4,
            wordBreak: 'break-word',
          }}>
            {rec.companyName || '—'}
          </Typography>
        </Box>

        {/* Sector */}
        <Tooltip title={fmtSector(rec.sector, lang)} placement="bottom" arrow>
          <Typography sx={{
            fontSize: '0.78rem', color: '#475569',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {fmtSector(rec.sector, lang)}
          </Typography>
        </Tooltip>

        {/* Market Cap */}
        <Typography sx={{
          fontSize: '0.78rem', color: '#64748b',
          textAlign: 'right', fontFamily: 'monospace', fontWeight: 500,
        }}>
          {fmtMCap(rec.marketCap || rec.mcap || rec.market_cap)}
        </Typography>

        {/* Score */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography sx={{
            fontSize: '0.85rem', fontWeight: 900, color: scoreColor,
            fontFamily: 'monospace', lineHeight: 1,
          }}>
            {rec.score?.toFixed(1) || '—'}
          </Typography>
        </Box>

        {/* Reasons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, minWidth: 0 }}>
          {showRowOverlay && (
            <Tooltip title={rowOverlay.text} placement="top" arrow>
              <Box sx={{ mb: 0.5 }}>
                <Chip
                  icon={<InfoOutlinedIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label={`${t('vnstock.recommendations.marketStage.styleAdjustment', 'Điều chỉnh theo pha thị trường')} ${rowOverlay.pct > 0 ? '+' : ''}${rowOverlay.pct.toFixed(1)}%`}
                  size="small"
                  sx={{
                    height: 24,
                    maxWidth: '100%',
                    fontSize: '0.66rem',
                    fontWeight: 700,
                    bgcolor: rowOverlay.positive ? alpha(activeCfg.color, 0.07) : '#fef2f2',
                    color: rowOverlay.positive ? activeCfg.color : '#b91c1c',
                    border: `1px solid ${rowOverlay.positive ? alpha(activeCfg.color, 0.18) : '#fecaca'}`,
                    '& .MuiChip-label': {
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              </Box>
            </Tooltip>
          )}
          {rec.reasons?.slice(0, 3).map((r, i) => {
            const txt = getReasonStr(r, lang);
            const isWarning = txt && (txt.startsWith('Cảnh báo') || txt.startsWith('Warning'));
            return txt ? (
              <Typography key={i} sx={{
                  fontSize: '0.73rem', color: isWarning ? '#dc2626' : '#475569', lineHeight: 1.5,
                  wordBreak: 'break-word',
                  fontWeight: isWarning ? 600 : 400,
                }}>
                  <Box component="span" sx={{ color: isWarning ? '#dc2626' : activeCfg.color, fontWeight: 700, mr: 0.5 }}>{isWarning ? '⚠' : '•'}</Box>
                  {txt}
              </Typography>
            ) : null;
          })}
          {/* Risk flags */}
          {rec.flags?.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.3 }}>
              {rec.flags.map((flag, i) => (
                <Box key={i} sx={{
                  fontSize: '0.62rem', fontWeight: 700, px: 0.75, py: 0.15,
                  borderRadius: 0.75, lineHeight: 1.3,
                  bgcolor: FLAG_COLORS[flag]?.bg || '#fef2f2',
                  color: FLAG_COLORS[flag]?.color || '#dc2626',
                }}>
                  {t(`flags.${flag}`, flag.replace(/_/g, ' '))}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  /* ── Mobile stock card ── */
  const renderMobileStock = (rec, idx) => {
    const scoreColor = getScoreColor(rec.score_level);
    const rowOverlay = getVisibleMarketStageOverlay(rec.marketStageOverlay, lang);
    const showRowOverlay = rowOverlay.visible && !sharedMarketStageOverlay;
    return (
      <Box
        key={rec.ticker || idx}
        onClick={() => goToStock(rec.ticker)}
        sx={{
          p: 1.5, mb: 1, borderRadius: 2,
          border: '1px solid #f1f5f9', cursor: 'pointer',
          transition: 'all 0.12s ease',
          '&:hover': { bgcolor: alpha(activeCfg.color, 0.02), borderColor: alpha(activeCfg.color, 0.2) },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700, minWidth: 18 }}>
              #{idx + 1}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: '#1e293b' }}>
              {rec.ticker}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: scoreColor, fontFamily: 'monospace' }}>
            {rec.score?.toFixed(1) || '—'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mb: 0.75 }}>
          {rec.companyName} · {fmtSector(rec.sector, lang)} · {fmtMCap(rec.marketCap || rec.mcap || rec.market_cap)}
        </Typography>
        {showRowOverlay && (
          <Tooltip title={rowOverlay.text} placement="top" arrow>
            <Box sx={{ mb: 0.8 }}>
              <Chip
                icon={<InfoOutlinedIcon sx={{ fontSize: '0.82rem !important' }} />}
                label={`${t('vnstock.recommendations.marketStage.styleAdjustment', 'Điều chỉnh theo pha thị trường')} ${rowOverlay.pct > 0 ? '+' : ''}${rowOverlay.pct.toFixed(1)}%`}
                size="small"
                sx={{
                  height: 24,
                  maxWidth: '100%',
                  fontSize: '0.64rem',
                  fontWeight: 700,
                  bgcolor: rowOverlay.positive ? alpha(activeCfg.color, 0.07) : '#fef2f2',
                  color: rowOverlay.positive ? activeCfg.color : '#b91c1c',
                  border: `1px solid ${rowOverlay.positive ? alpha(activeCfg.color, 0.18) : '#fecaca'}`,
                  '& .MuiChip-label': {
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </Box>
          </Tooltip>
        )}
        {rec.reasons?.slice(0, 2).map((r, i) => {
          const txt = getReasonStr(r, lang);
          const isWarning = txt && (txt.startsWith('Cảnh báo') || txt.startsWith('Warning'));
          return txt ? (
            <Typography key={i} sx={{
              fontSize: '0.72rem',
              color: isWarning ? '#dc2626' : '#475569',
              lineHeight: 1.4,
              fontWeight: isWarning ? 600 : 400,
            }}>
              <Box component="span" sx={{ color: isWarning ? '#dc2626' : activeCfg.color, fontWeight: 700, mr: 0.5 }}>{isWarning ? '⚠' : '•'}</Box>
              {txt}
            </Typography>
          ) : null;
        })}
        {rec.flags?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.4 }}>
            {rec.flags.slice(0, 3).map((flag, i) => (
              <Box key={i} sx={{
                fontSize: '0.58rem', fontWeight: 700, px: 0.6, py: 0.1,
                borderRadius: 0.5, lineHeight: 1.3,
                bgcolor: FLAG_COLORS[flag]?.bg || '#fef2f2',
                color: FLAG_COLORS[flag]?.color || '#dc2626',
              }}>
                {t(`flags.${flag}`, flag.replace(/_/g, ' '))}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  /* ── Empty state ── */
  const renderEmpty = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: '50%',
          bgcolor: alpha(activeCfg.color, 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 2,
        }}>
          {React.createElement(activeCfg.Icon, { sx: { fontSize: 26, color: activeCfg.color } })}
        </Box>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b', mb: 0.5 }}>
          {t('vnstock.recommendations.noData', 'Chưa có gợi ý')}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
          {t('vnstock.recommendations.dataUpdating', 'Dữ liệu đang được cập nhật')}
        </Typography>
      </Box>
    </Box>
  );

  /* ── Column headers ── */
  const COL_HEADERS = [
    { text: '#', align: 'left' },
    { text: t('vnstock.recommendations.table.ticker', 'Mã'), align: 'left' },
    { text: t('vnstock.recommendations.table.sector', 'Ngành'), align: 'left' },
    { text: t('vnstock.recommendations.table.marketCap', 'Vốn hóa'), align: 'right' },
    { text: t('vnstock.recommendations.table.score', 'Điểm'), align: 'center' },
    { text: t('vnstock.recommendations.table.reasons', 'Lý do đề xuất'), align: 'left' },
  ];

  const renderMarketStageSummary = () => {
    if (!activeMarketStage) return null;
    const compactText = getCompactMarketStageSummary(activeMarketStage, lang);

    return (
      <Box sx={{
        mx: isDesktop ? 2.5 : 0,
        mt: isDesktop ? 2 : 0.5,
        mb: 1.25,
        p: 1.2,
        borderRadius: 2,
        bgcolor: alpha(activeCfg.color, 0.04),
        border: `1px solid ${alpha(activeCfg.color, 0.12)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: 15, color: activeCfg.color, mt: 0.15, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65, alignItems: 'center', mb: 0.55 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#1e293b' }}>
                {t('vnstock.recommendations.marketStage.title', 'Pha thị trường hiện tại')}
              </Typography>
              {activeMarketStage?.stage && (
                <Chip
                  label={getMarketStageLabel(t, activeMarketStage.stage)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    bgcolor: alpha(activeCfg.color, 0.12),
                    color: activeCfg.color,
                  }}
                />
              )}
              {typeof activeMarketStage?.exposure_target_pct === 'number' && (
                <Chip
                  label={`${activeMarketStage.exposure_target_pct.toFixed(1)}%`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    borderColor: alpha(activeCfg.color, 0.18),
                    color: '#475569',
                    bgcolor: '#fff',
                  }}
                />
              )}
              {sharedMarketStageOverlay && (
                <Tooltip title={sharedMarketStageOverlay.text} placement="top" arrow>
                  <Chip
                    icon={<InfoOutlinedIcon sx={{ fontSize: '0.8rem !important' }} />}
                    label={`${sharedMarketStageOverlay.pct > 0 ? '+' : ''}${sharedMarketStageOverlay.pct.toFixed(1)}% ${t('vnstock.recommendations.marketStage.styleAdjustmentShort', 'điều chỉnh theo pha')}`}
                    size="small"
                    sx={{
                      height: 20,
                      maxWidth: '100%',
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      bgcolor: sharedMarketStageOverlay.positive ? alpha(activeCfg.color, 0.1) : '#fef2f2',
                      color: sharedMarketStageOverlay.positive ? activeCfg.color : '#b91c1c',
                      border: `1px solid ${sharedMarketStageOverlay.positive ? alpha(activeCfg.color, 0.16) : '#fecaca'}`,
                      '& .MuiChip-label': {
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#475569', lineHeight: 1.5 }}>
              {compactText}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  /* ═════════════════════════════════════════════════════════════
   * MOBILE LAYOUT — chips + card list
   * ═════════════════════════════════════════════════════════════ */
  if (!isDesktop) {
    return (
      <Box>
        <Box sx={{
          display: 'flex', gap: 0.75, overflowX: 'auto', p: 1.5, pb: 1,
          '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
        }}>
          {Object.entries(STRATEGIES).map(([style, sc]) => {
            const data = recommendations[style] || {};
            const count = (Array.isArray(data.results) ? data.results : []).length;
            const isActive = style === activeStyle;
            const name = data.name?.[lang] || t(sc.nameKey, style);

            return (
              <Chip
                key={style}
                icon={React.createElement(sc.Icon, { sx: { fontSize: 16 } })}
                label={`${name} (${count})`}
                size="small"
                onClick={() => setActiveStyle(style)}
                sx={{
                  flexShrink: 0, fontWeight: isActive ? 700 : 500, fontSize: '0.72rem',
                  bgcolor: isActive ? sc.color : 'transparent',
                  color: isActive ? '#fff' : sc.color,
                  border: `1.5px solid ${isActive ? sc.color : alpha(sc.color, 0.3)}`,
                  '&:hover': { bgcolor: isActive ? sc.color : alpha(sc.color, 0.08) },
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ p: 1.5, pt: 0.5 }}>
          {renderMarketStageSummary()}
          {results.length > 0 ? results.map(renderMobileStock) : renderEmpty()}
        </Box>
      </Box>
    );
  }

  /* ═════════════════════════════════════════════════════════════
   * DESKTOP LAYOUT — sidebar + stock list
   * ═════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ display: 'flex', minHeight: 480 }}>
      {/* ── Strategy Sidebar ── */}
      <Box sx={{
        width: 256, flexShrink: 0,
        borderRight: '1px solid #eef2f6',
        display: 'flex', flexDirection: 'column',
        bgcolor: '#fafbfd',
      }}>
        <Box sx={{ flex: 1, py: 0.5 }}>
          {Object.entries(STRATEGIES).map(([s, sc]) => renderSidebarItem(s, sc))}
        </Box>

        {/* Active strategy description */}
        {activeData.description?.[lang] && (
          <Box sx={{ p: 2, pt: 1.5, borderTop: '1px solid #eef2f6' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: activeCfg.color, mt: 0.3, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.6, fontStyle: 'italic' }}>
                {activeData.description[lang]}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Stock Content ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {renderMarketStageSummary()}
        {results.length > 0 ? (
          <>
            {/* Column headers */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '44px 1.4fr 0.9fr 0.6fr 60px 2.8fr',
              gap: '0 12px', alignItems: 'center',
              px: 2.5, py: 1.25, bgcolor: '#f8fafc',
              borderBottom: '2px solid #eef2f6',
            }}>
              {COL_HEADERS.map((h, i) => (
                <Typography key={i} sx={{
                  fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: h.align,
                }}>
                  {h.text}
                </Typography>
              ))}
            </Box>

            {/* Stock rows */}
            <Box sx={{ flex: 1 }}>
              {results.map(renderStockRow)}
            </Box>
          </>
        ) : renderEmpty()}
      </Box>
    </Box>
  );
};

export default RecommendationsView;
